/* eslint-disable @typescript-eslint/no-explicit-any */
import CustomComponent from '@ben/shared/components/benefit-sections/custom-component/custom-component';
import { HiddenMountContainer } from '@ben/shared/components/benefit-sections/hidden-mount-container';
import type {
  IMultiSelectOption,
  TNestedValue,
} from '@ben/shared/components/benefit-sections/multi-select-options/multi-select-options';
import { CustomComponentTypes } from '@ben/shared/constants';
import type { IModularBenefitComponent } from '@ben/shared/types/modular-benefit';
import {
  isComponentHiddenForAudience,
  type ModularBenefitAudience,
} from '@ben/shared/utils/modular-benefit-audience-visibility';
import { camelCase, isNil, mapKeys } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

const ContactCardSubComponents = ({
  contact,
  benefitId,
  disabled,
  contactSubComponentValues,
  onChildValueChange,
  backendErrors,
  showRequiredErrors,
  audience = 'employee',
}: {
  contact: IMultiSelectOption;
  benefitId: string | number;
  disabled?: boolean;
  contactSubComponentValues?: Record<string, any>[];
  onChildValueChange?: (
    parentKey: string,
    key: string,
    value: TNestedValue
  ) => void;
  backendErrors?: Record<string, unknown>;
  showRequiredErrors?: boolean;
  audience?: ModularBenefitAudience;
}) => {
  const { t } = useTranslation();

  const handleNestedComponentChange = useCallback(
    (
      parentKey: string,
      key: string,
      value: TNestedValue,
      type: CustomComponentTypes | string
    ) => {
      let newValue = value;

      newValue =
        type === CustomComponentTypes.MULTI_SELECT
          ? value
          : ((value as { target?: { value: TNestedValue } }).target?.value ??
            value);

      onChildValueChange?.(parentKey, key, newValue);
    },
    [onChildValueChange]
  );

  // Sync contact sub-components default values with the root state
  useEffect(() => {
    if (!contact.subComponents || !onChildValueChange) {
      return;
    }

    for (const subComponent of contact.subComponents) {
      const defaultValue = subComponent.default ?? subComponent.defaultValue;
      if (!isNil(defaultValue)) {
        const foundValue = contactSubComponentValues?.find(
          (childValue: Record<string, any>) =>
            childValue.hasOwnProperty(contact.key)
        )?.[contact.key];

        if (!foundValue || !foundValue.hasOwnProperty(subComponent.key)) {
          handleNestedComponentChange(
            contact.key,
            subComponent.key,
            defaultValue as TNestedValue,
            subComponent.type
          );
        }
      }
    }
  }, [
    contact.key,
    contact.subComponents,
    contactSubComponentValues,
    handleNestedComponentChange,
    onChildValueChange,
  ]);

  if (!contact.subComponents || contact.subComponents.length === 0) {
    return;
  }

  return (
    <ChildContainer>
      {contact.subComponents.map((subComponent: IModularBenefitComponent) => {
        const {
          key,
          id: subComponentFieldId,
          type: subComponentType,
          ...subComponentProps
        } = subComponent;
        const subComponentId = `${contact.key}-${key}`;
        const foundValue = contactSubComponentValues?.find(
          (childValue: Record<string, any>) =>
            childValue.hasOwnProperty(contact.key)
        )?.[contact.key];

        const camelCaseValues = foundValue
          ? mapKeys(foundValue, (_, foundKey) => camelCase(foundKey))
          : {};

        const isRequiredFieldMissing =
          !!showRequiredErrors &&
          !isComponentHiddenForAudience(subComponent, audience) &&
          !!subComponent.required &&
          !camelCaseValues[camelCase(key)];

        const backendFieldErrors =
          (backendErrors?.values as Record<string, unknown> | undefined)?.[
            camelCase(key)
          ] ??
          (backendErrors?.values as Record<string, unknown> | undefined)?.[key];

        const backendErrorMessage = Array.isArray(backendFieldErrors)
          ? backendFieldErrors[0]
          : undefined;

        const fieldErrors =
          backendErrorMessage ??
          (isRequiredFieldMissing ? t('phrases.requiredField') : undefined);

        return (
          <HiddenMountContainer
            key={key}
            $hidden={isComponentHiddenForAudience(subComponent, audience)}
          >
            <CustomComponent
              {...subComponentProps}
              type={subComponentType}
              id={subComponentId}
              fieldId={subComponentFieldId}
              benefitId={benefitId}
              onChange={(event: TNestedValue) =>
                handleNestedComponentChange(
                  contact.key,
                  key,
                  event,
                  subComponentType
                )
              }
              value={foundValue?.[key]}
              disabled={disabled}
              hasError={!!fieldErrors}
              errorMessage={
                typeof fieldErrors === 'string' ? fieldErrors : undefined
              }
            />
          </HiddenMountContainer>
        );
      })}
    </ChildContainer>
  );
};

export default ContactCardSubComponents;

const ChildContainer = styled.div<{ $disabled?: boolean }>`
  ${({ theme, $disabled }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    gap: ${theme.spacing(3)};
    padding: ${theme.spacing(3)};
    padding-top: 0;
    border: 1px solid ${theme.palette.neutral[60]};
    border-top: none;
    border-radius: 0 0 4px 4px;
    background: ${theme.palette.neutral[40]};

    ${$disabled &&
    css`
      padding-top: 0;
    `}
  `}
`;
